import { type Ref, useEffect, useRef, useState } from "react";
import { ArrowLeft, EllipsisVertical, Trash2 } from "lucide-react";
import { DeleteSessionDialog } from "./DeleteSessionDialog";

interface Props {
  title: string;
  onBack: () => void;
  onDelete: () => void;
  canDelete?: boolean;
  ref?: Ref<HTMLDivElement>;
}

export function ChatHeader({ title, onBack, onDelete, canDelete = true, ref }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <div className="chat-header-anchor" ref={ref}>
      <div className="chat-header">
        <button type="button" className="chat-header-back" onClick={onBack}>
          <ArrowLeft size={14} />
          <span>Назад</span>
        </button>
        <div className="chat-header-title" title={title}>
          {title}
        </div>
        <div className="chat-header-actions" ref={wrapRef}>
          {canDelete ? (
            <>
              <button
                type="button"
                className="chat-header-menu-trigger"
                aria-label="Действия"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              >
                <EllipsisVertical size={16} />
              </button>
              {menuOpen && (
                <div className="chat-header-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="chat-header-menu-item chat-header-menu-item--danger"
                    onClick={() => {
                      setMenuOpen(false);
                      setConfirmOpen(true);
                    }}
                  >
                    <Trash2 size={14} />
                    <span>Удалить чат</span>
                  </button>
                </div>
              )}
            </>
          ) : (
            // Reserve space symmetric to the back button so the title stays
            // optically centered in read-only sessions.
            <div className="chat-header-actions-spacer" aria-hidden />
          )}
        </div>
      </div>
      <DeleteSessionDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          onDelete();
        }}
      />
    </div>
  );
}
